/**
 * FranchiseOnboardingPage — `/franchise/applications/:id/onboard`.
 *
 * Step-through wizard reflecting the franchise state machine:
 *
 *   APPLIED → UNDER_REVIEW → APPROVED → TRAINING → LIVE
 *
 * The page loads the target franchise + its associated application (we read
 * the id from the URL — backend doesn't expose `/applications` listing yet,
 * so the route param can be either the application id or the franchise id;
 * we treat it as the franchise id and fetch via getFranchise).
 *
 * Each step calls a matching backend transition:
 *   - Submit:           POST /v1/franchise/franchises/:id/submit
 *   - Approve / Reject: POST /v1/franchise/franchises/applications/:id/review
 *                       (we resolve the latest open application via
 *                       GET /v1/franchise/franchises/applications?franchiseId=…
 *                       and prefill the id; operators can override manually
 *                       if they want to act on a historical application)
 *   - Provision device: POST /v1/franchise/franchises/:id/provision-device
 *   - Record training:  POST /v1/franchise/franchises/:id/record-training
 *   - Activate:         POST /v1/franchise/franchises/:id/activate
 */
import { type JSX, type ReactNode, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Circle } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  activateFranchise,
  getFranchise,
  listAllApplications,
  provisionDevice,
  recordTraining,
  reviewApplication,
  submitFranchiseApplication,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

type StepKey = 'submit' | 'review' | 'device' | 'training' | 'activate';

const STEPS: readonly { key: StepKey; label: string }[] = [
  { key: 'submit', label: 'Submit application' },
  { key: 'review', label: 'Review decision' },
  { key: 'device', label: 'Provision device' },
  { key: 'training', label: 'Record training' },
  { key: 'activate', label: 'Activate (go LIVE)' },
];

export function FranchiseOnboardingPage(): JSX.Element {
  const { id = '', applicationId = '' } = useParams<{ id?: string; applicationId?: string }>();
  const franchiseId = id || applicationId;
  const qc = useQueryClient();
  const [appId, setAppId] = useState(applicationId);
  const [reviewNote, setReviewNote] = useState('');
  const [deviceSerial, setDeviceSerial] = useState('');
  const [certRef, setCertRef] = useState('');
  const [actionErr, setActionErr] = useState<string | null>(null);

  const franchise = useQuery({
    queryKey: ['franchise', 'onboarding', franchiseId],
    queryFn: () => getFranchise(franchiseId),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  // Auto-resolve the latest application id for this franchise so operators
  // don't have to paste it. Falls back to manual entry if the lookup fails
  // or returns nothing (older franchises pre-applications-table).
  const applications = useQuery({
    queryKey: ['franchise', 'onboarding', 'applications', franchiseId],
    queryFn: () => listAllApplications({ franchiseId, pageSize: 10 }),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  const resolvedAppId = applications.data?.items[0]?.id ?? '';
  useEffect(() => {
    // Prefill once when we get a server-side id and the operator hasn't
    // started typing their own. We only assign when the field is empty so
    // we never clobber manual input.
    if (resolvedAppId && appId.length === 0) {
      setAppId(resolvedAppId);
    }
  }, [resolvedAppId, appId.length]);

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['franchise', 'onboarding', franchiseId] });
  };

  const submit = useMutation({
    mutationFn: () => submitFranchiseApplication(franchiseId),
    onSuccess: refresh,
    onError: (e: Error) => setActionErr(e.message),
  });
  const approve = useMutation({
    mutationFn: () => reviewApplication(appId.trim(), 'APPROVE', reviewNote.trim()),
    onSuccess: refresh,
    onError: (e: Error) => setActionErr(e.message),
  });
  const reject = useMutation({
    mutationFn: () => reviewApplication(appId.trim(), 'REJECT', reviewNote.trim()),
    onSuccess: refresh,
    onError: (e: Error) => setActionErr(e.message),
  });
  const provision = useMutation({
    mutationFn: () => provisionDevice(franchiseId, { serial: deviceSerial.trim() }),
    onSuccess: refresh,
    onError: (e: Error) => setActionErr(e.message),
  });
  const train = useMutation({
    mutationFn: () => recordTraining(franchiseId, certRef.trim()),
    onSuccess: refresh,
    onError: (e: Error) => setActionErr(e.message),
  });
  const activate = useMutation({
    mutationFn: () => activateFranchise(franchiseId),
    onSuccess: refresh,
    onError: (e: Error) => setActionErr(e.message),
  });

  if (franchiseId.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Franchise onboarding" />
        <p className="text-sm text-muted">Missing franchise id.</p>
      </div>
    );
  }

  if (franchise.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Franchise onboarding" />
        <p className="text-sm text-muted">Loading…</p>
      </div>
    );
  }
  if (franchise.isError) {
    const denied = franchise.error instanceof ApiError &&
      (franchise.error.status === 401 || franchise.error.status === 403);
    return (
      <div className="space-y-6">
        <PageHeader title="Franchise onboarding" />
        <DeferredNotice title={denied ? 'Permission required' : 'Service unreachable'}>
          {(franchise.error as Error).message}
        </DeferredNotice>
      </div>
    );
  }

  const f = franchise.data;
  const currentStep = stepFor(f.status);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Franchise onboarding"
        description={`${f.legalName} · ${f.franchiseNumber}`}
        actions={<StatusBadge status={f.status} />}
      />

      <ol className="bg-white border border-border rounded-xl p-4 grid grid-cols-1 sm:grid-cols-5 gap-2">
        {STEPS.map((step, idx) => {
          const done = STEPS.findIndex((s) => s.key === currentStep) > idx;
          const active = step.key === currentStep;
          return (
            <li key={step.key} className="flex items-center gap-2 text-xs">
              {done ? (
                <Check className="w-4 h-4 text-nipost-green" />
              ) : (
                <Circle className={`w-4 h-4 ${active ? 'text-nipost-green' : 'text-muted'}`} />
              )}
              <span className={active ? 'font-semibold text-gray-900' : 'text-muted'}>{step.label}</span>
            </li>
          );
        })}
      </ol>

      {currentStep === 'submit' ? (
        <StepCard title="Submit application">
          <p className="text-xs text-muted">
            Transitions <code className="font-mono">DRAFT</code> →{' '}
            <code className="font-mono">APPLIED</code>.
          </p>
          <button
            type="button"
            onClick={() => submit.mutate()}
            disabled={submit.isPending}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            Submit
          </button>
        </StepCard>
      ) : null}

      {currentStep === 'review' ? (
        <StepCard title="Review decision">
          {resolvedAppId ? (
            <p className="text-xs text-muted">
              Acting on application{' '}
              <code className="font-mono text-gray-700">{resolvedAppId}</code>.
              You can override the id below to act on a different application
              for this franchise.
            </p>
          ) : applications.isPending ? (
            <p className="text-xs text-muted">Looking up application id…</p>
          ) : (
            <DeferredNotice title="No application found for this franchise">
              <p>
                The applications listing returned no rows for{' '}
                <code className="font-mono">{franchiseId}</code>. Paste the
                application id below if you have it from another channel.
              </p>
            </DeferredNotice>
          )}
          <input
            type="text"
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            placeholder="Application id…"
            className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
          />
          <textarea
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
            placeholder="Decision note (required)…"
            rows={3}
            className="w-full px-3 py-2 border border-border rounded-lg text-sm"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => approve.mutate()}
              disabled={approve.isPending || appId.trim().length === 0 || reviewNote.trim().length === 0}
              className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
            >
              Approve
            </button>
            <button
              type="button"
              onClick={() => reject.mutate()}
              disabled={reject.isPending || appId.trim().length === 0 || reviewNote.trim().length === 0}
              className="px-3 py-2 border border-red-300 text-red-700 text-sm font-semibold rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              Reject
            </button>
          </div>
        </StepCard>
      ) : null}

      {currentStep === 'device' ? (
        <StepCard title="Provision device">
          <p className="text-xs text-muted">
            Records the counter device profile for the approved franchise.
          </p>
          <input
            type="text"
            value={deviceSerial}
            onChange={(e) => setDeviceSerial(e.target.value)}
            placeholder="Device serial / ref…"
            className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
          />
          <button
            type="button"
            onClick={() => provision.mutate()}
            disabled={provision.isPending || deviceSerial.trim().length === 0}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            Provision
          </button>
        </StepCard>
      ) : null}

      {currentStep === 'training' ? (
        <StepCard title="Record training">
          <p className="text-xs text-muted">
            Transitions <code className="font-mono">APPROVED</code> →{' '}
            <code className="font-mono">TRAINING</code>.
          </p>
          <input
            type="text"
            value={certRef}
            onChange={(e) => setCertRef(e.target.value)}
            placeholder="Certificate ref…"
            className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
          />
          <button
            type="button"
            onClick={() => train.mutate()}
            disabled={train.isPending || certRef.trim().length === 0}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            Record training
          </button>
        </StepCard>
      ) : null}

      {currentStep === 'activate' ? (
        <StepCard title="Activate franchise">
          <p className="text-xs text-muted">
            Transitions <code className="font-mono">TRAINING</code> →{' '}
            <code className="font-mono">LIVE</code>. The franchise can begin transacting.
          </p>
          <button
            type="button"
            onClick={() => activate.mutate()}
            disabled={activate.isPending}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            Go LIVE
          </button>
        </StepCard>
      ) : null}

      {currentStep === null ? (
        <div className="bg-white border border-border rounded-xl p-5">
          <p className="text-sm text-gray-700">
            This franchise is in the <strong>{f.status}</strong> state — no further
            onboarding action is required. Lifecycle changes happen on the franchise detail page.
          </p>
        </div>
      ) : null}

      {actionErr ? <p className="text-sm text-danger">{actionErr}</p> : null}
    </div>
  );
}

function stepFor(status: string): StepKey | null {
  switch (status) {
    case 'DRAFT': return 'submit';
    case 'APPLIED':
    case 'UNDER_REVIEW': return 'review';
    case 'APPROVED': return 'device';
    case 'TRAINING': return 'training';
    case 'LIVE': return null;
    default: return null;
  }
}

function StepCard({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <section className="bg-white border border-border rounded-xl p-5 space-y-3">
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      {children}
    </section>
  );
}
